'use client';

import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';

import { cn } from '../lib';

/**
 * Root container for a select menu, based on Radix UI's Select primitive.
 */
function Select({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Root>) {
  return <SelectPrimitive.Root data-slot="select" {...props} />;
}

/**
 * Groups related select items together.
 */
function SelectGroup({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Group>) {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />;
}

/**
 * The element that displays the currently selected value.
 */
function SelectValue({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Value>) {
  return <SelectPrimitive.Value data-slot="select-value" {...props} />;
}

/**
 * The element that triggers the select menu to open.
 */
function SelectTrigger({
  className,
  size = 'default',
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> & {
  /** The height variant of the trigger. */
  size?: 'sm' | 'default';
}) {
  return (
    <SelectPrimitive.Trigger
      className={cn(
        `border-input data-[placeholder]:text-muted-foreground
        [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring
        focus-visible:ring-ring/50 aria-invalid:ring-destructive/20
        dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive
        dark:bg-input/30 dark:hover:bg-input/50 shadow-xs
        *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex
        *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2
        [&_svg:not([class*='size-'])]:size-5 flex w-fit items-center justify-between
        gap-2 whitespace-nowrap rounded-md border bg-transparent px-3 py-2
        text-sm outline-none transition-[color,box-shadow] focus-visible:ring-[3px]
        disabled:cursor-not-allowed disabled:opacity-50 data-[size=default]:h-10
        data-[size=sm]:h-9 [&_svg]:pointer-events-none [&_svg]:shrink-0`,
        className
      )}
      data-size={size}
      data-slot="select-trigger"
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDownIcon className="size-5 opacity-50" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

/**
 * Button to scroll up within the select content.
 */
function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
  return (
    <SelectPrimitive.ScrollUpButton
      className={cn(
        'flex cursor-default items-center justify-center py-1',
        className
      )}
      data-slot="select-scroll-up-button"
      {...props}
    >
      <ChevronUpIcon className="size-5" />
    </SelectPrimitive.ScrollUpButton>
  );
}

/**
 * Button to scroll down within the select content.
 */
function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
  return (
    <SelectPrimitive.ScrollDownButton
      className={cn(
        'flex cursor-default items-center justify-center py-1',
        className
      )}
      data-slot="select-scroll-down-button"
      {...props}
    >
      <ChevronDownIcon className="size-5" />
    </SelectPrimitive.ScrollDownButton>
  );
}

/**
 * The content container for the select menu.
 */
function SelectContent({
  className,
  children,
  position = 'popper',
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        className={cn(
          `bg-popover text-popover-foreground data-[state=open]:animate-in
          data-[state=closed]:animate-out data-[state=closed]:fade-out-0
          data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95
          data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2
          data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2
          data-[side=top]:slide-in-from-bottom-2 max-h-(--radix-select-content-available-height)
          origin-(--radix-select-content-transform-origin) relative z-50 min-w-[8rem]
          overflow-y-auto overflow-x-hidden rounded-md border shadow-md`,
          position === 'popper' &&
            `data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1
            data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1`,
          className
        )}
        data-slot="select-content"
        position={position}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            'p-1',
            position === 'popper' &&
              `h-[var(--radix-select-trigger-height)] w-full
              min-w-[var(--radix-select-trigger-width)] scroll-my-1`
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

/**
 * A label for a select group.
 */
function SelectLabel({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Label>) {
  return (
    <SelectPrimitive.Label
      className={cn('text-muted-foreground px-2 py-1.5 text-xs', className)}
      data-slot="select-label"
      {...props}
    />
  );
}

/**
 * An individual item within the select menu.
 */
function SelectItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item>) {
  return (
    <SelectPrimitive.Item
      className={cn(
        `focus:bg-accent focus:text-accent-foreground
        [&_svg:not([class*='text-'])]:text-muted-foreground outline-hidden
        [&_svg:not([class*='size-'])]:size-5 *:[span]:last:flex
        *:[span]:last:items-center *:[span]:last:gap-2 relative flex w-full
        cursor-pointer select-none items-center gap-2 rounded-md py-2 pe-8 ps-2
        text-sm data-[disabled]:pointer-events-none data-[disabled]:opacity-50
        [&_svg]:pointer-events-none [&_svg]:shrink-0`,
        className
      )}
      data-slot="select-item"
      {...props}
    >
      <span className="size-4 absolute end-2 flex items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="size-5" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

/**
 * A divider between select items or groups.
 */
function SelectSeparator({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) {
  return (
    <SelectPrimitive.Separator
      className={cn('bg-border pointer-events-none -mx-1 my-1 h-px', className)}
      data-slot="select-separator"
      {...props}
    />
  );
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
